import { createTipCheckout, TipAmount } from "@serophin/nottldr-shared";
import { Beer, Coffee, Hamburger, Heart } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";
import type { PageProps } from "../pageProps";

type TipOption = { amount: TipAmount; price: string; label: string; icon: ComponentChildren };

const TIP_OPTIONS: TipOption[] = [
  { amount: TipAmount.Coffee, price: "$5", label: "Coffee", icon: <Coffee class="w-8 h-8" /> },
  { amount: TipAmount.Beer, price: "$10", label: "Beer", icon: <Beer class="w-8 h-8" /> },
  { amount: TipAmount.Lunch, price: "$20", label: "Lunch", icon: <Hamburger class="w-8 h-8" /> },
];

const PAGE = "min-h-screen rounded-2xl bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100";
const LINK = "text-blue-600 dark:text-blue-400 hover:underline";

const ThankYou = () => (
  <div class={PAGE}>
    <div class="px-6 py-12 max-w-2xl mx-auto">
      <div class="text-center">
        <div class="flex justify-center mb-6">
          <Heart class="w-16 h-16 text-red-500 fill-red-500" />
        </div>
        <h1 class="text-3xl md:text-4xl font-bold mb-6">Thank you!</h1>
        <p class="text-xl text-gray-600 dark:text-gray-300 mb-4">
          Your support means the world to me. It helps keep !tldr running and motivates me to keep building.
        </p>
        <p class="text-lg text-gray-500 dark:text-gray-400">
          <a href="/" class={`${LINK} visible-signedin`}>
            Back to reading
          </a>
          <a href="/articles" class={`${LINK} visible-anonymous`}>
            Back to reading
          </a>
        </p>
      </div>
    </div>
  </div>
);

type OptionProps = { option: TipOption; loading: TipAmount | null; onTip: (amount: TipAmount) => void };

const Spinner = () => <div class="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />;

const TipButton = ({ option, loading, onTip }: OptionProps) => (
  <button
    type="button"
    onClick={() => onTip(option.amount)}
    disabled={loading !== null}
    class="flex flex-col items-center justify-center p-3 rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 hover:border-indigo-400 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    data-test-click="tip"
    data-test-option={option.amount}
  >
    <div class="mb-2">{loading === option.amount ? <Spinner /> : option.icon}</div>
    <span class="font-bold mb-1">{option.label}</span>
    <span class="text-sm">{option.price}</span>
  </button>
);

const Intro = () => (
  <section class="text-center mb-12">
    <h1 class="text-3xl md:text-2xl font-bold mb-6">Tip me!</h1>
    <div class="space-y-4 text-gray-600 dark:text-gray-300">
      <p>!tldr is built and maintained by a solo developer. No team, no investors, no ads.</p>
      <p>If you're finding value in what you read here, a tip helps cover the costs and keeps this project going.</p>
      <p>No pressure at all. The fact that you're here reading already means a lot to me.</p>
    </div>
  </section>
);

const StripeNote = () => (
  <section class="text-center text-sm text-gray-500 dark:text-gray-400">
    <p>
      All prices in USD. Payments are processed securely by{" "}
      <a href="https://stripe.com" class="underline" target="_blank" rel="noopener noreferrer">
        Stripe
      </a>
      .
    </p>
  </section>
);

const useTip = () => {
  const [loading, setLoading] = useState<TipAmount | null>(null);
  const toast = useToast();
  const tip = async (amount: TipAmount) => {
    setLoading(amount);
    try {
      window.location.href = await createTipCheckout(amount);
    } catch (error) {
      const { status, message } = (error ?? {}) as { status?: number; message?: string };
      toast.error(status === 429 && message ? message : "Oops! This is embarrassing 😬 Failed to create tip checkout session. Please try again.");
    } finally {
      setLoading(null);
    }
  };
  return { loading, tip };
};

export const TipPage = (_props: PageProps) => {
  const { loading, tip } = useTip();
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setSuccess(new URLSearchParams(window.location.search).get("success") === "true");
  }, []);

  if (success) {
    return <ThankYou />;
  }
  return (
    <div class={PAGE}>
      <div class="px-3 py-6 max-w-2xl mx-auto">
        <Intro />
        <section class="text-center mb-3">
          <p class="text-lg font-bold text-gray-900 dark:text-white">Buy me a...</p>
        </section>
        <section class="grid grid-cols-3 gap-2 mb-12">
          {TIP_OPTIONS.map((option) => (
            <TipButton key={option.amount} option={option} loading={loading} onTip={tip} />
          ))}
        </section>
        <StripeNote />
      </div>
    </div>
  );
};
